import React from 'react';
import { useAuth } from '../contexts/AuthContext';

// Footer component for all pages
// Shows full footer with columns before login, simplified footer after login
const Footer = () => {
  const { isAuthenticated } = useAuth();
  const authenticated = isAuthenticated();

  // Simplified footer for authenticated users
  if (authenticated) {
    return (
      <footer className="app-footer app-footer-simplified">
        {/* Bottom section with copyright and policies */}
        <div className="footer-main-content">
          <div className="footer-bottom">
            <div className="footer-bottom-content">
              <span className="footer-copyright">© 2025 Photoshoot AI, a product of Qressy Solutions Private Limited. All rights reserved.</span>
              <div className="footer-policy-links">
                <a href="/privacy-policy" className="footer-policy-link">Privacy Policy</a>
                <span className="footer-policy-separator">|</span>
                <a href="/tos" className="footer-policy-link">Terms of Service</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  // Full footer for non-authenticated users
  return (
    <footer className="app-footer">
      {/* Purple top bar */}
      <div className="footer-top-bar"></div>
      
      {/* Main footer content */}
      <div className="footer-main-content">
        <div className="footer-columns">
          {/* Column 1: Photoshoot AI */}
          <div className="footer-column">
            <h3 className="footer-column-heading">Photoshoot AI</h3>
            <p className="footer-column-description">
              Revolutionizing jewelry photography with AI technology. Create professional photoshoots in seconds, not days.
            </p>
          </div>

          {/* Column 2: Jewelry Photography */}
          <div className="footer-column">
            <h3 className="footer-column-heading">Jewelry Photography</h3>
            <ul className="footer-link-list">
              <li><a href="/about" className="footer-link">Professional Jewelry Photography</a></li>
              <li><a href="/" className="footer-link">AI Jewelry Photoshoot</a></li>
              <li><a href="/" className="footer-link">Jewelry Product Photography</a></li>
              <li><a href="/" className="footer-link">Gold Jewelry Photography</a></li>
              <li><a href="/" className="footer-link">Diamond Ring Photography</a></li>
              <li><a href="/" className="footer-link">Wedding Jewelry Photoshoot</a></li>
              <li><a href="/" className="footer-link">Bridal Jewelry Photography</a></li>
            </ul>
          </div>

          {/* Column 3: Support */}
          <div className="footer-column">
            <h3 className="footer-column-heading">Support</h3>
            <ul className="footer-link-list">
              <li><a href="/contact" className="footer-link">Help Center</a></li>
              <li><a href="/contact" className="footer-link">Contact Us</a></li>
              <li><a href="/refunds" className="footer-link">Refund Policy</a></li>
              <li><a href="/about" className="footer-link">About Us</a></li>
            </ul>
          </div>

          {/* Column 4: Follow on Social Media */}
          <div className="footer-column">
            <h3 className="footer-column-heading">Follow on Social Media</h3>
            <div className="footer-social-links">
              <a href="https://www.instagram.com/getphotoshoot.ai?igsh=N2Zham5oOHUzenli" target="_blank" rel="noopener noreferrer" className="footer-social-link" aria-label="Instagram">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a href="https://www.twitter.com" target="_blank" rel="noopener noreferrer" className="footer-social-link" aria-label="Twitter">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/>
                </svg>
              </a>
              <a href="https://www.youtube.com" target="_blank" rel="noopener noreferrer" className="footer-social-link" aria-label="YouTube">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom section with copyright and policies */}
        <div className="footer-bottom">
          <div className="footer-divider"></div>
          <div className="footer-bottom-content">
            <span className="footer-copyright">© 2025 Photoshoot AI, a product of Qressy Solutions Private Limited. All rights reserved.</span>
            <div className="footer-policy-links">
              <a href="/privacy-policy" className="footer-policy-link">Privacy Policy</a>
              <span className="footer-policy-separator">|</span>
              <a href="/tos" className="footer-policy-link">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

